import type { LayerStyle, MapLayer } from '@trimble/geospatial';

export type LayersUpdater = (
  update: (layers: MapLayer[]) => MapLayer[]
) => void;

export interface LayerStyleOverrides {
  setLayerStyle(layerIds: readonly string[], style: Partial<LayerStyle>): void;
  apply(layer: MapLayer): MapLayer;
}

/** Kept apart from the layer list so a layer reload does not drop session edits. */
export function createLayerStyleOverrides(
  updateLayers: LayersUpdater
): LayerStyleOverrides {
  const overrides = new Map<string, Partial<LayerStyle>>();

  return {
    setLayerStyle(layerIds, style): void {
      const ids = new Set(layerIds);
      for (const layerId of ids) {
        overrides.set(layerId, { ...overrides.get(layerId), ...style });
      }
      // Store setters merge into existing objects, and loaded layers can share
      // one style object (e.g. the default style), so replace instead.
      updateLayers((layers) =>
        layers.map((layer) =>
          ids.has(layer.id)
            ? { ...layer, style: { ...layer.style, ...style } }
            : layer
        )
      );
    },
    apply(layer): MapLayer {
      const override = overrides.get(layer.id);
      return override
        ? { ...layer, style: { ...layer.style, ...override } }
        : layer;
    },
  };
}
